import { useTranslation } from 'react-i18next';
import { calculateDiffFromToday } from 'utils/datetime.utils';
import { usePTPConfigContext } from 'GlobalConfig.context';
import type Shipment from 'interfaces/PmParcel';
import { formatDeliveryDate } from '../EstimatedDeliveryDate.utils';
import {
  shouldShowEddByStage,
  hasValidEdd,
  isEddTimeRange,
  resolveEddFormats,
} from '../utils';
import { useEddPriority } from './useEddPriority';
import { useEddVisibility } from './useEddVisibility';

interface UseEddParams {
  parcel: Shipment;
  orgId: number;
}

interface UseEddReturn {
  isLoading: boolean;
  shouldDisplay: boolean;
  title: string;
  description: string;
}

/**
 * Public facade for EDD display logic.
 * Callers only need `parcel` and `orgId` — config and theme are read from PTP context.
 * Returns render-ready `title` and `description` strings.
 */
export const useEdd = ({ parcel, orgId }: UseEddParams): UseEddReturn => {
  const { t } = useTranslation();

  // Step 1: Read EDD config and theme from PTP context.
  // Centralised here so callers (the component) pass only parcel + orgId.
  const config = usePTPConfigContext();
  const eddConfig = config?.parcelDetailsBox?.estimatedDeliveryDate;
  const eddPriorityOrder = eddConfig?.priorityOrder ?? [];
  const isDisplayed = eddConfig?.isDisplayed ?? false;
  const shouldShowForOutForDelivery =
    eddConfig?.shouldShowForOutForDelivery ?? false;
  const dateDisplayFormat = eddConfig?.dateDisplayFormat ?? '';
  const timeDisplayFormat = eddConfig?.timeDisplayFormat ?? '';
  const isDisplayedNumDaysLeft = eddConfig?.isDisplayedNumDaysLeft ?? false;
  const timezone = config?.timezone ?? '';

  // Step 2: First guard — skip all fetching when the config toggle is off or the
  // parcel stage makes EDD irrelevant (e.g. delivered, out_for_delivery without flag).
  // Passed into useEddPriority so it can bail out of the fetch loop immediately.
  const isEddEligible =
    isDisplayed &&
    shouldShowEddByStage(parcel?.stage ?? '', shouldShowForOutForDelivery);

  // Step 3: Fetch & prioritize — walks the priority order and returns the first EDD
  // source that yields a valid date. Skips all fetching when isEddEligible is false.
  const { prioritizedEdd, isLoading: eddLoading } = useEddPriority({
    parcel,
    eddPriorityOrder: Array.isArray(eddPriorityOrder)
      ? eddPriorityOrder
      : [eddPriorityOrder],
    orgId,
    isEddEligible,
  });

  // Step 4: Visibility gate — evaluates all "should this EDD show?" rules:
  // config toggle, stage gate, date validity, event gate (carrier), time gate.
  const shouldDisplay = useEddVisibility({
    eddValue: prioritizedEdd,
    parcel,
    isDisplayEnabled: isDisplayed,
    shouldShowForOutForDelivery,
  });

  // Step 5: Derive date shape from prioritized EDD.
  // isLoading proxies eddLoading; dates are null until loading settles and all gates pass.
  const isLoading = eddLoading;
  const hasEdd = !eddLoading && hasValidEdd(prioritizedEdd) && shouldDisplay;
  const startDate = hasEdd ? prioritizedEdd!.first : null;
  const isTimeRange = hasEdd ? isEddTimeRange(prioritizedEdd) : false;
  const endDate = isTimeRange ? prioritizedEdd!.last : null;

  // Guard exit: hooks above must always run (React hook rules), but bail out here
  // before any formatting when EDD is ineligible or has nothing to display.
  if (!isEddEligible || isLoading || !shouldDisplay || !startDate?.time) {
    return { isLoading, shouldDisplay, title: '', description: '' };
  }

  // Step 6: Resolve format strings — source-specific overrides applied before formatting.
  const { resolvedDateFormat, resolvedTimeFormat } = resolveEddFormats({
    source: prioritizedEdd?.source,
    startDate,
    endDate,
    timezone,
    dateDisplayFormat,
    timeDisplayFormat,
  });

  // Step 7: Format — produce the EDD date text.
  const title = formatDeliveryDate({
    startDate,
    endDate,
    timezone,
    dateDisplayFormat: resolvedDateFormat,
    timeDisplayFormat: resolvedTimeFormat,
  });

  // Step 8: Description — show days-left copy when applicable, otherwise fall back
  // to the generic "Estimated delivery" label.
  const numDaysLeft = calculateDiffFromToday(startDate.time, 'days', true);
  const description =
    !isTimeRange && numDaysLeft > 1 && isDisplayedNumDaysLeft
      ? t('NUMBER_DAYS_LEFT', { numbers: Math.ceil(numDaysLeft) })
      : t('ESTIMATED_DELIVERY');

  return { isLoading, shouldDisplay, title, description };
};
